import {
  Component, Input, SimpleChanges, OnInit, OnDestroy, ViewEncapsulation,
  ChangeDetectionStrategy, ChangeDetectorRef, HostListener, ViewChild, ElementRef
} from '@angular/core';
import { LanguageService } from '../../../services/language.service';
import { Language } from '../../../models/language';
import { ReportService } from "../../../services/report.service";
import { ActivatedRoute } from "@angular/router";
import { saveAs } from 'file-saver';
import { MatDialog } from '@angular/material/dialog';
import { PrivacySettingsModalComponent } from '../../shared/privacy-settings-modal/privacy-settings-modal.component';
import { OfflineReportModalComponent } from '../../shared/offline-report-modal/offline-report-modal.component';
import { UserService } from 'src/app/services/user.service';
import { HttpClient } from '@angular/common/http';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';


@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent implements OnInit, OnDestroy {
  title = 'Banner';
  languages: Language[] = [];
  activeMenuBar: { [key: string]: boolean } = {};
  @Input() bannerDetails: any;
  @Input() submissionDetails: any;
  @Input() studentDeletionPermissions: any;
  @Input() isDocumentInArchive: any;
  @Input() sourcesAreDeleted: any;
  @Input() isNotStudentPrecheck: any;
  @Input() initialSources: any;
  @Input() allManipulations: any;
  @Input() metadata: any;
  @Input() imagesDetectionData: any;
  @Input() sentenceDetails: any;
  submissionId: string = ''
  newTab!: string;
  userInfo: any;
  showExpandView = false;
  homeLink: string
  isOnIA;
  isLogoClickable = true;
  calculatedFileSize: string = '';
  private destroy$ = new Subject<void>();

  @ViewChild('closeSubmissionDetails') closeSubmissionDetailsBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('detailsTriggerBtn') detailsTriggerBtn!: ElementRef<HTMLButtonElement>;
  @ViewChild('downloadTriggerBtn') downloadTriggerBtn!: ElementRef<HTMLButtonElement>;

  constructor(
    private languageService: LanguageService,
    private reportService: ReportService,
    private acRouter: ActivatedRoute,
    private dialog: MatDialog,
    private userService: UserService,
    private http: HttpClient,
    private spinnerService: NgxSpinnerService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService
  ) { }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscKey(event: KeyboardEvent) {
    if (this.activeMenuBar['submissionDetails']) {
      this.closeMenuBar('submissionDetails', event);
    }
    if (this.activeMenuBar['download']) {
      this.closeMenuBar('download', event);
    }
    if (this.activeMenuBar['settings']) {
      this.closeMenuBar('settings', event);
    }
  }

  @HostListener('document:click', ['$event'])
  handleGlobalClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isMenubarElement = target.closest('.menubar') !== null;
    const isMenuBarButton = target.closest('[data-menu-bar-key]') !== null;
    if (!isMenubarElement && !isMenuBarButton) {
      this.closeMenuBar('submissionDetails');
      this.closeMenuBar('download');
      this.closeMenuBar('settings');
    }
  }

  ngOnInit(): void {
    this.languageService.getLanguages().subscribe((languages) => {
      this.languages = languages;
    });

    this.acRouter.params.subscribe((data: any) => {
      this.submissionId = data.id ? data.id : data.submissionId;
    })
    // this.newTab = environment.new_tab_url;
    this.newTab = window.location.href;


    document.addEventListener('click', (event) => this.handleGlobalClick(event));

    let iframeContext = JSON.parse(localStorage.getItem('iframeContext') || '{}');

    this.showExpandView = iframeContext.isInIframe || iframeContext.isThirdParty;

    // Check if the route has  a  parameter called  clientName
    if (this.acRouter.snapshot.params.clientName) {
      this.isOnIA = true;
    } else {
      this.isOnIA = false;
    }

    this.isLogoClickable = this.isOnIA && !iframeContext.parentOrigin?.toLowerCase().includes('inspera');

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['submissionDetails'] && changes['submissionDetails'].currentValue !== changes['submissionDetails'].previousValue) {
      if (!this.isOnIA) {
        this.userService.userInfo(this.submissionDetails.submission.userId, localStorage.getItem('ACCESS_TOKEN')).subscribe((data: any) => {
          this.userInfo = data.user;
        });
      }

      if (this.submissionDetails?.fileUrl) {
        this.fetchFileSize(this.submissionDetails.fileUrl);
      }
    }
  }

  private fetchFileSize(fileUrl: string): void {
    this.http.head(fileUrl, { observe: 'response' }).subscribe(
      (response) => {
        const contentLength = response.headers.get('Content-Length');
        if (contentLength) {
          const sizeInBytes = parseInt(contentLength, 10);
          this.calculatedFileSize = this.formatBytes(sizeInBytes);
          this.cdr.markForCheck();
        }
      },
      (error) => {
        console.log('HEAD request failed, trying alternative method', error);
        this.fetchFileSizeAlternative(fileUrl);
      }
    );
  }

  private fetchFileSizeAlternative(fileUrl: string): void {
    this.http.get(fileUrl, { responseType: 'blob' }).subscribe(
      (blob: Blob) => {
        this.calculatedFileSize = this.formatBytes(blob.size);
        this.cdr.markForCheck();
      },
      (error) => {
        console.error('Failed to fetch file size', error);
        this.calculatedFileSize = 'N/A';
        this.cdr.markForCheck();
      }
    );
  }

  getLanguageName(languageCode: string | string[]): string {
    let languageCodes: string[];
    if (typeof languageCode === 'string' && languageCode.startsWith('["')) {
      try {
        languageCodes = JSON.parse(languageCode);
      } catch (error) {
        console.error('Invalid JSON string:', languageCode);
        return 'Invalid Language Code';
      }
    } else if (typeof languageCode === 'string') {
      languageCodes = [languageCode];
    } else {
      languageCodes = languageCode;
    }

    const languageNames = languageCodes.map(code => this.languageService.getLocalizedLanguageName(code));

    return languageNames.join(', ');
  }

  openLink(url: string): void {
    if (url.includes('support.inspera.com')) {
      window.open(url, '_blank');
    } else {
      const userToken = localStorage.getItem('ACCESS_TOKEN') as string;
      const clientName = this.acRouter.snapshot.params['clientName'] || 'default';
      const submissionId = this.submissionId;
      const tokenWithoutQuotes = userToken.slice(1, -1);
      const sessionId = this.acRouter.snapshot.queryParams['sessionId'];
      let query = 'expanded=true';
      if (sessionId) {
        query += `&sessionId=${sessionId}`;
      }

      let newUrl = '';

      if (this.isOnIA) {
        newUrl = `${window.location.origin}/report/${clientName}/${submissionId}/${tokenWithoutQuotes}?${query}`;
      } else {
        newUrl = `${window.location.origin}/submission/${submissionId}/report/v2/${tokenWithoutQuotes}?${query}`;
      }
      window.open(newUrl, '_blank');
    }
  }

  toggleMenuBar(menuBar: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const isOpening = !this.activeMenuBar[menuBar];

    Object.keys(this.activeMenuBar).forEach((key) => {
      this.activeMenuBar[key] = false;
    });

    this.activeMenuBar[menuBar] = isOpening;
    this.cdr.markForCheck();

    if (menuBar === 'submissionDetails' && isOpening) {
      requestAnimationFrame(() => {
        this.closeSubmissionDetailsBtn?.nativeElement.focus();
      });
    } else if (!isOpening && (menuBar === 'submissionDetails' || menuBar === 'download')) {
      this.returnFocusToMenuTrigger(menuBar);
    }
  }

  onMenuKeydown(event: KeyboardEvent, menuBar: string): void {
    if (!this.activeMenuBar[menuBar]) {
      return;
    }

    const menu = (event.currentTarget as HTMLElement)
      .closest('[role="menu"]');

    if (!menu) {
      return;
    }

    const items = Array.from(
      menu.querySelectorAll<HTMLElement>('[role="menuitem"]')
    );

    const currentIndex = items.indexOf(
      document.activeElement as HTMLElement
    );

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = (currentIndex + 1) % items.length;
        items[nextIndex].focus();
        break;

      case 'ArrowUp':
        event.preventDefault();
        nextIndex =
          (currentIndex - 1 + items.length) % items.length;
        items[nextIndex].focus();
        break;

      case 'Home':
        event.preventDefault();
        items[0].focus();
        break;

      case 'End':
        event.preventDefault();
        items[items.length - 1].focus();
        break;
    }
  }

  openDownloadMenu(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    const isOpening = !this.activeMenuBar['download'];

    this.toggleMenuBar('download');

    if (isOpening) {
      setTimeout(() => {
        const firstItem = document.querySelector(
          '#download-menu [role="menuitem"]'
        ) as HTMLElement | null;

        firstItem?.focus();
      });
    }
  }

  closeMenuBar(menubar: string, event?: Event, returnFocus = !!event): void {
    if (event) {
      event.stopPropagation();
    }

    const wasOpen = this.activeMenuBar[menubar];
    this.activeMenuBar[menubar] = false;
    this.cdr.markForCheck();

    if (returnFocus && wasOpen) {
      this.returnFocusToMenuTrigger(menubar);
    }
  }

  private returnFocusToMenuTrigger(menubar: string): void {
    requestAnimationFrame(() => {
      let trigger: HTMLButtonElement | undefined;

      if (menubar === 'submissionDetails') {
        trigger = this.detailsTriggerBtn?.nativeElement;
      } else if (menubar === 'download') {
        trigger = this.downloadTriggerBtn?.nativeElement;
      }

      if (!trigger) {
        return;
      }

      trigger.focus({ preventScroll: true });
      trigger.dispatchEvent(new FocusEvent('focus', { bubbles: true }));
    });
  }

  downloadOriginalDocument() {
    // this.spinnerService.show()
    this.reportService
      .downloadOriginalDocument(this.submissionId)
      .pipe()
      .subscribe(
        (data: any) => {
          saveAs(data.urlToDownload, 'document.pdf');
          // this.spinnerService.hide()
        },
        (error: any) => {
          console.log('error', error);
          // this.spinnerService.hide()
        }
      );
  }

  downloadPdfReport(reportType: string): void {
    const modalRef = this.dialog.open(OfflineReportModalComponent, {
      width: '600px',
      disableClose: false,
    });

    modalRef.afterClosed().subscribe((result) => {
      if (!result) return;

      this.spinnerService.show();
      this.reportService
        .downloadReport(this.submissionId, reportType, this.isOnIA, result.includeSentenceFindings)
        .pipe()
        .subscribe(
          (data: any) => {
            const desiredName = `Offline Report - ${this.bannerDetails.title}.pdf`;
            this.http.get(data.presignedS3UrlDownload, { responseType: 'arraybuffer' }).subscribe((fileData: ArrayBuffer) => {
              const blob = new Blob([fileData]);
              saveAs(blob, desiredName);
            });
            this.spinnerService.hide();
          },
          (error) => {
            console.log('error', error);
            this.spinnerService.hide();
          }
        );
    });
  }

  downloadAiReport(): void {
    // this.spinnerService.show()
    this.reportService
      .downloadAiReport(this.submissionId)
      .pipe()
      .subscribe(
        (data: any) => {
          saveAs(data.presignedS3UrlDownload, 'ai-report.pdf');
          // this.spinnerService.hide()
        },
        (error) => {
          console.log('error', error);
          // this.spinnerService.hide()
        }
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('click', this.handleGlobalClick);
  }




  openSettingsModal(): void {
    const excludedHiddenTextCount = this.sentenceDetails?.filter((sentence: any) =>
      sentence.characters && sentence.characters.length > 0 &&
      (sentence.whitecharactersExcluded === true || sentence.whitecharacterExcluded === true)
    ).reduce((total: number, sentence: any) => total + (sentence.characters?.length || 0), 0) || 0;

    this.dialog.open(PrivacySettingsModalComponent, {
      width: '900px',
      height: '800px',
      ariaLabel: 'Document Settings',
      data: {
        userInfo: this.userInfo,
        studentDeletionPermissions: this.studentDeletionPermissions,
        isDocumentInArchive: this.isDocumentInArchive,
        sourcesAreDeleted: this.sourcesAreDeleted,
        isNotStudentPrecheck: this.isNotStudentPrecheck,
        submission: this.submissionDetails.submission,
        reportData: {
          excludedOriginalSources: this.initialSources?.excludedOriginalSources || [],
          excludedTranslatedSources: this.initialSources?.excludedSourcesMlPlag || [],
          manipulations: this.allManipulations || {},
          metadata: this.metadata,
          imagesDetectionData: this.imagesDetectionData || [],
          excludedHiddenTextCount: excludedHiddenTextCount
        }
      }
    });
  }

  goToHome(): void {

    if (this.acRouter.snapshot.params.id) {
      let url = environment.ioApplicationUrl
      window.open(url, '_self');
    }
  }

  formatBytes(bytes: number | string): string {
    if (bytes === null || bytes === undefined) {
      return 'N/A';
    }

    if (typeof bytes === 'string' && /[a-zA-Z]/.test(bytes)) {
      return bytes
        .replace(/\bbytes?\b/gi, 'Bytes')
        .replace(/\bkb\b/gi, 'KB')
        .replace(/\bmb\b/gi, 'MB')
        .replace(/\bgb\b/gi, 'GB')
        .replace(/\btb\b/gi, 'TB');
    }

    const numBytes = typeof bytes === 'string' ? parseFloat(bytes) : bytes;

    if (isNaN(numBytes)) {
      return 'N/A';
    }

    const marker = 1024;
    const decimal = 2;
    const kiloBytes = marker;
    const megaBytes = marker * marker;
    const gigaBytes = marker * marker * marker;

    if (numBytes < kiloBytes) return numBytes + ' Bytes';
    else if (numBytes < megaBytes)
      return (numBytes / kiloBytes).toFixed(decimal) + ' KB';
    else if (numBytes < gigaBytes)
      return (numBytes / megaBytes).toFixed(decimal) + ' MB';
    else
      return (numBytes / gigaBytes).toFixed(decimal) + ' GB';
  }
}
